import { Deal, Recommendations, StrapiID } from '@ca/common/types'
import { validateItem } from '@order/utils/validateItem'
import { StepOption } from '@order/steps/types'
import { StepCtx } from '@order/steps/useStepCtx'

type StepItem = {
  id: StrapiID
  variable: string
  active: {
    id: StrapiID
    value: string
  }
}

type RecommendationItem = {
  id: StrapiID
  value: string
}

type RecommendCtx = Pick<StepCtx, 'config' | 'refraction' | 'values'>

const getRecommendation = (
  items: RecommendationItem[],
  rec: Recommendations.Option,
  ctx: RecommendCtx
) => {
  const { config, refraction } = ctx

  if (!config) {
    return
  }

  const validate = (item: RecommendationItem) => {
    return (
      rec.value === item.value &&
      validateItem(rec, ctx.values, config.conditions, refraction ?? undefined).result
    )
  }

  return items.find(validate)
}

export type RecommendationItemOption = RecommendationItem & {
  option: Recommendations.Option
}

const groupRecommendationItems = (
  items: RecommendationItem[],
  variable: string,
  ctx: Pick<StepCtx, 'config' | 'refraction' | 'values' | 'flowName'>
) => {
  if (!ctx.config) {
    console.log('No config')
    return undefined
  }
  const recommendations = ctx.config.recommendations?.[variable]
  if (!recommendations) {
    // console.log('No recommendations', ctx.config.recommendations, variable)
    return undefined
  }

  const options = recommendations
    // Only show recommendations for the current flow
    .filter((recommendation) => recommendation.flows?.includes(ctx.flowName))
    .flatMap((recommendations) => recommendations.options)

  const rows: RecommendationItemOption[][] = []

  let finalId = undefined
  let hasRecommendations = false

  const recommendedIds = new Set<StrapiID>()

  // Start with all items
  let rest = items.map((item) => item.id)

  for (const option of options) {
    const rec = getRecommendation(items, option, ctx)
    if (rec) {
      hasRecommendations = true
    }

    if (!rec || recommendedIds.has(rec.id)) {
      continue
    }
    recommendedIds.add(rec.id)
    // Remove from the rest
    rest = rest.filter((id) => id !== rec.id)
    // If rec is final we skip all other recs
    if (option.final) {
      finalId = rec.id
    }
    // Chained items get pushed to new row
    if (option.chain) {
      rows.push([
        {
          ...rec,
          option,
        },
      ])
      continue
    }
    const currentRow = rows.length
    if (currentRow === 0) {
      rows.push([
        {
          ...rec,
          option,
        },
      ])
      continue
    }
    // Push recommendation to current row
    rows[currentRow - 1].push({
      ...rec,
      option,
    })
  }

  return {
    hasRecommendations,
    finalId,
    // Recommended items in rows
    recommend: rows,
    // If we have a final item then we don't show not recommend items
    rest: finalId != undefined ? [] : rest,
  }
}

const getRecommendationItems = (
  items: RecommendationItem[],
  variable: string,
  ctx: Pick<StepCtx, 'config' | 'refraction' | 'values' | 'flowName'>
) => {
  if (!ctx.config) {
    console.log('No config')
    return undefined
  }
  const recommendations = ctx.config.recommendations?.[variable]
  if (!recommendations) {
    console.log('No recommendations', ctx.config.recommendations, variable)
    return undefined
  }

  const options = recommendations
    // Only show recommendations for the current flow
    .filter((recommendation) => recommendation.flows?.includes(ctx.flowName))
    .flatMap((recommendations) => recommendations.options)

  const res: RecommendationItemOption[] = []

  let finalId = undefined
  let hasRecommendations = false

  const recommendedIds = new Set<StrapiID>()

  // Start with all items
  let rest = items.map((item) => item.id)

  for (const option of options) {
    const rec = getRecommendation(items, option, ctx)
    if (rec) {
      hasRecommendations = true
    }

    if (!rec || recommendedIds.has(rec.id)) {
      continue
    }
    recommendedIds.add(rec.id)
    // Remove from the rest
    rest = rest.filter((id) => id !== rec.id)
    // If rec is final we skip all other recs
    if (option.final) {
      finalId = rec.id
    }
    res.push({
      ...rec,
      option,
    })
  }

  return {
    hasRecommendations,
    finalId,
    // Recommended items in rows
    recommend: res,
    // If we have a final item then we don't show not recommend items
    rest: finalId != undefined ? [] : rest,
  }
}

export type ItemWithRecommendation = {
  recommendation?: Recommendations.Option
}

export type RecommendationStepOption = StepOption & ItemWithRecommendation

export type RecommendationDealItem = Deal & ItemWithRecommendation

const toStepOptions = (
  recommendations: ReturnType<typeof groupRecommendationItems>,
  options: StepOption[]
) => {
  const rows: RecommendationStepOption[][] = []

  const map = new Map(
    options.map((option) => [option.variantId ?? option.id, option])
  )
  recommendations?.recommend.forEach((row) => {
    const optionRow = row
      .map((rec) => {
        const item = map.get(rec.id)
        if (!item) {
          return undefined
        }
        return {
          ...item,
          recommendation: rec.option,
        } satisfies RecommendationStepOption
      })
      .filter((v) => !!v)
    if (optionRow.length) {
      rows.push(optionRow)
    }
  })
  const restOptions = recommendations?.rest
    .map((id) => map.get(id))
    .filter((v) => !!v)
  if (restOptions?.length) {
    rows.push(restOptions)
  }

  return rows
}

const toDeals = (
  recommendations: ReturnType<typeof groupRecommendationItems>,
  deals: Deal[]
) => {
  const rows: RecommendationDealItem[][] = []

  const map = new Map(deals.map((deal) => [deal.id, deal]))
  recommendations?.recommend.forEach((row) => {
    const optionRow = row
      .map((rec) => {
        const deal = map.get(rec.id)
        if (!deal) {
          return undefined
        }
        return {
          ...deal,
          recommendation: rec.option,
        } satisfies ItemWithRecommendation
      })
      .filter((v) => !!v)
    if (optionRow.length) {
      rows.push(optionRow)
    }
  })
  const restOptions = recommendations?.rest
    .map((id) => map.get(id))
    .filter((v) => !!v)
  if (restOptions?.length) {
    rows.push(restOptions)
  }

  return rows
}

const groupStepItems = (
  step: {
    items: StepItem[]
  },
  variable: string,
  ctx: Pick<StepCtx, 'config' | 'refraction' | 'values' | 'flowName'>
) => {
  const items = step.items.map((item) => ({
    id: item.active.id,
    value: item.active.value,
  }))
  return groupRecommendationItems(items, variable, ctx)
}

const groupDeals = (
  deals: Deal[],
  ctx: Pick<StepCtx, 'config' | 'refraction' | 'values' | 'flowName'>
) => {
  const items = deals.map((deal) => ({
    id: deal.id,
    value: deal.code,
  }))

  return groupRecommendationItems(items, 'deal', ctx)
}

const getStepRecommendations = (
  step: {
    items: StepItem[]
  },
  variable: string,
  ctx: Pick<StepCtx, 'config' | 'refraction' | 'values' | 'flowName'>
) => {
  const items = step.items.map((item) => ({
    id: item.active.id,
    value: item.active.value,
  }))
  return getRecommendationItems(items, variable, ctx)
}

const isRecommendation = (
  recommendations: ReturnType<typeof groupRecommendationItems>,
  rows: ItemWithRecommendation[][],
  row: number
) => {
  if (!recommendations) {
    return false
  }
  return (
    recommendations.hasRecommendations &&
    row <= recommendations.recommend.length - 1 &&
    rows[row].every((item) => !item.recommendation?.noRecommendation)
  )
}

export type RecommendationsGrouped = ReturnType<typeof groupRecommendationItems>

export type Recommendations = ReturnType<typeof getRecommendationItems>

const isRecommendedOption = (
  recommendations: Recommendations | undefined,
  option: StepOption
) => {
  if (!recommendations) {
    return false
  }
  const id = option.variantId || option.id
  return recommendations?.recommend[0]?.id === id
}

const findRecommendedOption = (
  recommendations: Recommendations | undefined,
  options: StepOption[]
) => {
  if (!recommendations) {
    return undefined
  }
  return options.find((option) => isRecommendedOption(recommendations, option))
}

export const RecommendUtils = {
  groupItems: groupStepItems,
  getStepRecommendations,
  groupDeals,
  toStepOptions,
  toDeals,
  isRecommendation,
  options: {
    isRecommended: isRecommendedOption,
    findRecommended: findRecommendedOption,
  },
}
